import { ClientEvents } from 'common/events/ClientEvents';
import { StorageKeys } from 'common/storage/constants';
import { QueryParams } from 'common/urls/QueryParams';
import { Slugs } from 'common/urls/Slugs';
import QueryString from 'query-string';
import { useContext, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router';
import { useMealPlanSurfacingLogic } from 'src/pages/consumer/meal-plans/hooks/useMealPlanSurfacingLogic';
import { useMarketSegmentContent } from 'src/rebrand/Homepage/useMarketSegmentContent';
import { useLocation } from 'src/shared/hooks/useLocation';
import { marketSegmentFromParams, useMarketSegment } from 'src/shared/hooks/useMarketSegment';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useUtm } from 'src/shared/hooks/useUtm';
import { Routes } from 'src/shared/Routes';
import { useLocalStorage } from 'src/shared/storage/Storage';
import { getSluggedPath } from 'src/shared/utils/RouteUtilities';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';
import { StringParam, useQueryParam } from 'use-query-params';
// 10/7/24 when we deprecate rebrand/homepage, pull this logic over
import { useHomepageRedirects } from '../../../rebrand/Homepage/useHomepageRedirects';
import { ZipCodeSelectionModalContext } from '../components/header-v2/ZipCodeSelection/ZipCodeSelectionModalContext';
import { HomepageHeroSection } from './components/HomepageHeroSection';
import { MadeNotManufactured } from './components/MadeNotManufactured';
import { ProductStepsSection } from './components/ProductStepsSection';
import { ReviewsSection } from './components/ReviewsSection';
import { ShefCarousel } from './components/ShefCarousel';
import { TopValueCalloutSection } from './components/TopValueCalloutSection';

const Homepage = () => {
  const location = useLocation();
  const history = useHistory();
  const utmParams = useUtm({ location });
  const tracker = useTracker();
  const [community] = useQueryParam(QueryParams.COMMUNITY, StringParam);
  const { isInMealPlanRegion, navigateToMealPlanPreferences, setVisitedMealPlans } = useMealPlanSurfacingLogic();
  const { showZipCodeModal } = useContext(ZipCodeSelectionModalContext);
  const [, setVisitedExplorePage] = useLocalStorage<boolean>(StorageKeys.VISITED_EXPLORE_PAGE, false);
  const utmCampaign = utmParams?.campaign;
  const utmTerm = utmParams?.term;
  const { setMarketSegment, marketSegment } = useMarketSegment();
  const { exploreQueryParams } = useMarketSegmentContent(marketSegment, false);
  useHomepageRedirects({ exploreQueryParams });
  useEffect(
    () => setMarketSegment(marketSegmentFromParams({ utmCampaign, utmTerm, community })),
    [utmCampaign, utmTerm, setMarketSegment, community]
  );
  const [cachedZipCode, , zipCodeMetaData] = useCachedZipCode();
  const regionId = useMemo(() => zipCodeMetaData?.cachedZipCodeData?.zipCode?.region?.id ?? 0, [zipCodeMetaData]);
  /**
   * Redirects user to explore page or waitlist based on zip code availability.
   * Used when user already has a cached zip code and clicks explore/meal plan buttons.
   *
   * @private Internal helper for immediate redirects when zip is already cached
   */
  const redirectToExplore = () => {
    const { availableToCustomers } = zipCodeMetaData?.cachedZipCodeData?.zipCode ?? {};

    const route = !availableToCustomers
      ? Routes.WAITLIST_HOME
      : getSluggedPath(
          Routes.CONSUMER_EXPLORE,
          [
            {
              slug: Slugs.ZIP_CODE,
              value: cachedZipCode ?? '',
            },
          ],
          { ...QueryString.parse(history.location.search) }
        );
    history.push(route);
  };

  /**
   * Handles explore button clicks on homepage.
   *
   * REDIRECT FLOW:
   * - If no cached zip: Opens modal with redirectOnSubmit=true (default)
   *   → useZipCodeInput handles redirect directly after zip submission
   * - If cached zip exists: Redirects immediately to explore page
   *
   * @param placement - UI placement for tracking (defaults to 'hero')
   */
  const exploreButtonOnClick = () => {
    tracker.track(ClientEvents.HOMEPAGE_MARKETPLACE_CLICK, {
      placement: 'hero',
    });
    if (!cachedZipCode) {
      // 10/26/24 Zip modal redirects, so set visitedExplorePage to avoid a mealPlanUrl from mealPlanSurfacingLogic
      setVisitedExplorePage(true);
      showZipCodeModal({ isMealPlan: false, showCloseButton: true });
      return;
    }

    redirectToExplore();
  };

  /**
   * Handles meal plan button clicks on homepage.
   *
   * TWO-LAYER REDIRECT ARCHITECTURE:
   *
   * Layer 1 - No cached region:
   * - Opens modal with redirectOnSubmit=false
   * - useZipCodeInput caches zip but returns early (no redirect)
   * - Modal closes, zip is now cached in global state
   *
   * Layer 2 - useHomepageRedirects reacts to global state:
   * - Detects new cached zip code
   * - Triggers redirect to meal plan intake or explore page
   * - This reactive pattern allows homepage to control meal plan routing logic
   *
   * Direct redirect (when region already cached):
   * - If mealPlanIntakeRouteByRegion exists: redirect to meal plan intake
   * - Otherwise: redirect to explore page
   *
   * @param placement - UI placement for tracking (defaults to 'hero')
   */
  const mealPlanButtonOnClick = (placement?: string) => {
    tracker.track(ClientEvents.HOMEPAGE_MEAL_PLAN_CLICK, {
      placement: placement ?? 'hero',
    });
    if (!regionId) {
      setVisitedMealPlans(true);
      // Add mealPlanIntent to current URL so useHomepageRedirects can detect user intent
      const currentParams = QueryString.parse(history.location.search);
      const newUrl = QueryString.stringifyUrl({
        url: history.location.pathname,
        query: { ...currentParams, [QueryParams.MEAL_PLAN_INTENT]: 'true' },
      });
      history.replace(newUrl);

      showZipCodeModal({
        isMealPlan: true,
        redirectOnSubmit: false, // Let useHomepageRedirects handle the redirect
        showCloseButton: true,
      });
      return;
    }

    if (isInMealPlanRegion) {
      navigateToMealPlanPreferences({});
      return;
    }

    // Add mealPlanIntent to indicate user wanted meal plans but will be redirected to explore
    const { availableToCustomers } = zipCodeMetaData?.cachedZipCodeData?.zipCode ?? {};
    const route = !availableToCustomers
      ? Routes.WAITLIST_HOME
      : getSluggedPath(
          Routes.CONSUMER_EXPLORE,
          [
            {
              slug: Slugs.ZIP_CODE,
              value: cachedZipCode ?? '',
            },
          ],
          { ...QueryString.parse(history.location.search), [QueryParams.MEAL_PLAN_INTENT]: 'true' }
        );
    history.push(route);
  };

  return (
    <>
      <Helmet>
        <link rel='canonical' href='https://shef.com' />
      </Helmet>
      <HomepageHeroSection
        mealPlanButtonOnClick={() => mealPlanButtonOnClick('hero')}
        exploreButtonOnClick={exploreButtonOnClick}
      />
      <ProductStepsSection />
      <ReviewsSection />
      <TopValueCalloutSection mealPlanButtonOnClick={() => mealPlanButtonOnClick('meal-plan-info')} />
      <ShefCarousel />
      <MadeNotManufactured />
    </>
  );
};

export default Homepage;
